import { HTMLAttributes, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { authenticate } from '@/api/auth'
import { useTranslation } from 'react-i18next';
import i18n, { loadAuthenticatedTranslations, loadLanguageFromBackend } from '@/i18n';
import { toast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/authStore'
import { User, Lock, Eye, EyeOff, ArrowLeft, ArrowRight } from 'lucide-react'

type UserAuthFormProps = HTMLAttributes<HTMLDivElement>

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const nav = useNavigate();

  const formSchema = z.object({
    username: z
      .string()
      .min(1, { message: i18n.t('enter_user') }),
    password: z
      .string()
      .min(1, {
        message: i18n.t('enter_pass'),
      }),
    remember_me: z.boolean().default(false)
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
      remember_me: false,
    },
  })

  function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true)

    authenticate(
      data.username,
      data.password,
      data.remember_me,
      undefined,
      undefined,
      i18n.language // locale from current i18n
    )
      .then(v => {
        if (v?.access != null) {
          const state = useAuthStore.getState();
          state.auth.setAccessToken(v.access);
          state.auth.setRefreshToken(v.refresh);
          state.auth.setUser(v.user)

          const redirectAfterLogin = () => {
            if (v.email_verification_required) {
              nav({ to: "/auth/verify-email" });
            } else if (v.approval_status && v.approval_status !== 'approved') {
              nav({ to: "/auth/pending-approval" });
            } else {
              nav({ to: "/dashboard" });
            }
          }

          loadLanguageFromBackend()
            .then(() => loadAuthenticatedTranslations())
            .then(redirectAfterLogin)
            .catch(redirectAfterLogin);
        } else {
          setIsLoading(false);
          toast({
            variant: 'destructive',
            title: i18n.t('serv_wrong_resp')
          })
        }
      })
      .catch(e => {
        setIsLoading(false);
        toast({
          variant: 'destructive',
          title: e?.response?.data?.error != null ? e.response.data.error : i18n.t('serv_error')
        })
      });
  }

  const isRTL = i18n.dir() === 'rtl';

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

          {/* Username Field */}
          <FormField
            control={form.control}
            name='username'
            render={({ field }) => (
              <FormItem className='space-y-2'>
                <FormLabel className="text-sm font-semibold text-gray-700 dark:text-gray-300 block">
                  {t('user')}
                </FormLabel>
                <div className="relative">
                  <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-gray-400`}>
                    <User className="w-5 h-5" />
                  </span>
                  <FormControl>
                    <Input
                      className={cn(
                        "w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl py-6 text-gray-800 dark:text-white focus-visible:ring-2 focus-visible:ring-secondary focus-visible:border-transparent transition-all outline-none text-sm placeholder-gray-400",
                        isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
                      )}
                      placeholder={t('user_placeholder') || "example@law-office.com"}
                      autoFocus
                      autoComplete='username'
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password Field */}
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem className='space-y-2'>
                <FormLabel className="text-sm font-semibold text-gray-700 dark:text-gray-300 block">
                  {t('pass')}
                </FormLabel>
                <div className="relative">
                  <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-gray-400`}>
                    <Lock className="w-5 h-5" />
                  </span>
                  <FormControl>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      className={cn(
                        "w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl py-6 text-gray-800 dark:text-white focus-visible:ring-2 focus-visible:ring-secondary focus-visible:border-transparent transition-all outline-none text-sm placeholder-gray-400",
                        isRTL ? "pr-10 pl-10" : "pl-10 pr-10"
                      )}
                      placeholder='••••••••'
                      autoComplete='current-password'
                      {...field}
                    />
                  </FormControl>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className='flex justify-end mt-1'>
                  <Link
                    to='/auth/forgot-password'
                    className='text-xs font-medium text-primary hover:text-secondary dark:text-blue-300 dark:hover:text-secondary transition-colors'
                  >
                    {t('forgot_pass')}
                  </Link>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Remember Me & Submit */}
          <div className="space-y-6">
            <FormField
              control={form.control}
              name='remember_me'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center space-x-2 space-y-0 rtl:space-x-reverse'>
                  <FormControl>
                    <Checkbox
                      id='remember_me'
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor='remember_me'
                    className='text-sm font-normal cursor-pointer select-none text-gray-600 dark:text-gray-400'
                  >
                    {t('remember_me', { defaultValue: 'Remember me' })}
                  </FormLabel>
                </FormItem>
              )}
            />

            <Button
              className='w-full bg-primary hover:bg-primary-dark text-white font-bold py-6 rounded-xl shadow-lg shadow-blue-900/20 hover:shadow-blue-900/40 transition-all transform active:scale-[0.98] flex items-center justify-center gap-2 group'
              disabled={isLoading}
            >
              <span>{t('login')}</span>
              {isRTL ? (
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              ) : (
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              )}
            </Button>
          </div>

          <div className='mt-8 text-center'>
            <p className='text-sm text-gray-500 dark:text-gray-400'>
              {t('dont_have_account', { defaultValue: "Don’t have an account?" })}{' '}
              <Link to='/auth/signup' className='font-bold text-secondary hover:text-yellow-600 transition-colors mr-1'>
                {t('signup')}
              </Link>
            </p>
          </div>

        </form>
      </Form>
    </div>
  )
}
